import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskLine, Reveal } from "../components/Reveal";
import SEO, { SEO_CONFIG } from "../components/SEO";
import { getBlog } from "../lib/api";
import { useLanguage } from "../i18n/LanguageContext";

export default function Blog() {
  const { t, lang } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getBlog()
      .then((data) => setPosts(Array.isArray(data) ? data : []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const featured = posts[0];
  const rest = posts.slice(1);

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": SEO_CONFIG.blog.title,
    "description": SEO_CONFIG.blog.description,
    "url": SEO_CONFIG.blog.canonical,
    "publisher": {
      "@type": "Organization",
      "name": "Ashlar Buildchem",
      "logo": "https://www.ashlarbuildchem.com/logo.png"
    }
  };

  return (
    <div className="pt-20" data-testid="blog-page">
      <SEO {...SEO_CONFIG.blog} schema={blogSchema} />
      <section className="mx-auto max-w-[1600px] px-6 pb-16 pt-20 md:px-10 md:pt-28">
        <span className="label text-primary">{t("blog.eyebrow")}</span>
        <h1
          className={`mt-6 font-display text-4xl font-bold md:text-8xl ${
            lang === "hi"
              ? "normal-case leading-[1.15] tracking-normal"
              : "uppercase leading-[0.85] tracking-tighter"
          }`}
        >
          <MaskLine delay={0.05}>{t("blog.title")}</MaskLine>
        </h1>
      </section>

      {loading && (
        <div className="flex min-h-[300px] items-center justify-center py-20" data-testid="blog-loading">
          <span className="label animate-pulse text-muted-foreground">{t("blog.loading")}</span>
        </div>
      )}

      {!loading && posts.length === 0 && (
        <div className="flex min-h-[300px] flex-col items-center justify-center py-20 text-center" data-testid="blog-empty">
          <p className="font-display text-2xl text-muted-foreground">{t("blog.notFound")}</p>
        </div>
      )}

      {!loading && featured && (
        <section className="mx-auto max-w-[1600px] px-6 md:px-10">
          <Reveal>
            <Link
              to={`/blog/${featured.slug}`}
              data-testid="blog-featured"
              className="group grid gap-8 border-y border-black/10 py-10 md:grid-cols-2 md:items-center md:gap-16"
            >
              <div className="clip-corner overflow-hidden">
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="h-72 w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0 md:h-[440px]"
                />
              </div>
              <div>
                <div className="flex items-center gap-4">
                  <span className="label text-primary">{featured.category}</span>
                  <span className="text-xs text-muted-foreground">{featured.read_time}</span>
                </div>
                <h2 className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tight md:text-5xl">
                  {featured.title}
                </h2>
                <p className="mt-5 max-w-xl text-base text-muted-foreground md:text-lg">{featured.excerpt}</p>
                <span className="mt-8 inline-flex items-center gap-2 text-foreground">
                  <span className="label">{t("blog.readArticle")}</span>
                  <ArrowUpRight className="h-4 w-4 text-primary transition-transform duration-200 group-hover:rotate-45" />
                </span>
              </div>
            </Link>
          </Reveal>
        </section>
      )}

      {!loading && rest.length > 0 && (
        <section className="mx-auto max-w-[1600px] px-6 py-20 md:px-10 md:py-28" data-testid="blog-list">
          <div className="grid gap-px bg-black/10 md:grid-cols-3">
            {rest.map((post, i) => (
              <Reveal key={post.slug} delay={i * 0.08} className="bg-background">
                <Link
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-post-${post.slug}`}
                  className="group flex h-full flex-col p-8 transition-colors duration-300 hover:bg-secondary md:p-10"
                >
                  <div className="mb-8 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="h-52 w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="label text-primary">{post.category}</span>
                    <span className="text-xs text-muted-foreground">{post.read_time}</span>
                  </div>
                  <h3 className="mt-4 font-display text-2xl font-semibold leading-tight">{post.title}</h3>
                  <p className="mt-3 flex-1 text-sm text-muted-foreground">{post.excerpt}</p>
                  <ArrowUpRight className="mt-6 h-5 w-5 text-muted-foreground transition-all duration-200 group-hover:rotate-45 group-hover:text-primary" />
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
